import {
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { PageSidebar, PageSidebarHost } from '@asset-metrix/portal-ui';
import clsx from 'clsx';
import { useShallow } from 'zustand/react/shallow';

import { useStore } from '../../store';
import { useAiAssistantChrome } from '../ai-assistant/AiAssistantChromeContext';

import { PageSidebarOutlet } from './PageSidebarOutlet';
import { getPageSidebarActiveKey, PageSidebarScope } from './types';

import styles from './PageSidebarLayout.module.scss';

export type PageSidebarLayoutProps = {
  children: ReactNode;
  navigationKey?: string | null;
  scope?: PageSidebarScope;
  topNavigation?: ReactNode;
};

function hasPageNavigationChanged(
  previousKey: string | null | undefined,
  nextKey: string | null | undefined
): boolean {
  if (previousKey === nextKey) {
    return false;
  }

  if (previousKey == null && nextKey != null) {
    return false;
  }

  return true;
}

export function PageSidebarLayout({
  children,
  navigationKey = null,
  scope = 'page',
  topNavigation,
}: PageSidebarLayoutProps) {
  const { t } = useTranslation();
  const [
    closePageSidebar,
    isPageSidebarPinned,
    pageSidebarWidth,
    setPageSidebarPinned,
    setPageSidebarWidth,
  ] = useStore(
    useShallow((state) => [
      state.closePageSidebar,
      state.isPageSidebarPinned,
      state.pageSidebarWidth,
      state.setPageSidebarPinned,
      state.setPageSidebarWidth,
    ])
  );
  const isFullscreen = scope === 'fullscreen';
  const opened = useStore((state) =>
    isFullscreen ? state.fullscreenPageSidebar.opened : state.pageSidebar.opened
  );
  const active = useStore((state) =>
    isFullscreen ? state.fullscreenPageSidebar.active : state.pageSidebar.active
  );
  const { isOpen: assistantOpen, isPinned: assistantPinned } =
    useAiAssistantChrome();
  const pinPreference = isFullscreen ? true : isPageSidebarPinned;
  const previousNavigationKey = useRef(navigationKey);
  const navigationKeyChanged =
    !isFullscreen &&
    hasPageNavigationChanged(previousNavigationKey.current, navigationKey);
  const visibleActive = navigationKeyChanged ? null : active;
  const visibleOpened = navigationKeyChanged ? false : opened;
  const overlayFill =
    !isFullscreen && assistantOpen && visibleActive != null && visibleOpened;
  const pinningEnabled = !isFullscreen && !overlayFill;
  const hostPinned = pinPreference && visibleActive != null && !overlayFill;
  const fixedPageLayout =
    !isFullscreen && (topNavigation != null || hostPinned);
  const scrollContainedHost =
    !isFullscreen && topNavigation != null && !hostPinned;

  useEffect(() => {
    if (isFullscreen) {
      return;
    }

    document.body.classList.toggle(
      'with-page-sidebar--scroll-contained',
      fixedPageLayout
    );

    return () => {
      document.body.classList.remove('with-page-sidebar--scroll-contained');
    };
  }, [fixedPageLayout, isFullscreen]);

  useLayoutEffect(() => {
    if (isFullscreen) {
      return;
    }

    if (navigationKeyChanged) {
      closePageSidebar('page');
    }

    previousNavigationKey.current = navigationKey;
  }, [closePageSidebar, isFullscreen, navigationKey, navigationKeyChanged]);

  useEffect(() => {
    if (!isFullscreen) {
      return;
    }

    return () => {
      closePageSidebar('fullscreen');
    };
  }, [closePageSidebar, isFullscreen]);

  useEffect(() => {
    if (overlayFill && assistantPinned && isPageSidebarPinned) {
      setPageSidebarPinned(false);
    }
  }, [assistantPinned, isPageSidebarPinned, overlayFill, setPageSidebarPinned]);

  const handleClose = useCallback(() => {
    closePageSidebar(scope);
  }, [closePageSidebar, scope]);

  const handlePinnedChange = useCallback(
    (nextPinned: boolean) => {
      if (isFullscreen) {
        return;
      }
      setPageSidebarPinned(nextPinned);
    },
    [isFullscreen, setPageSidebarPinned]
  );

  const sidebar =
    visibleActive != null ? (
      <PageSidebar
        fillRemaining={overlayFill}
        fullscreen={isFullscreen}
        onClose={handleClose}
        onPinnedChange={handlePinnedChange}
        onSidebarWidthChange={setPageSidebarWidth}
        open={visibleOpened}
        pinningEnabled={pinningEnabled}
        pinned={overlayFill ? false : pinPreference}
        sidebarWidth={pageSidebarWidth ?? undefined}
        title={t('app.pageSidebar.widgetTitle')}
      >
        <PageSidebarOutlet
          key={getPageSidebarActiveKey(visibleActive)}
          active={visibleActive}
          opened={visibleOpened}
          scope={scope}
        />
      </PageSidebar>
    ) : null;

  return (
    <div
      className={clsx({
        [styles['layout--fullscreen']]: isFullscreen,
        [styles['layout--fullscreen-pinned']]: isFullscreen && hostPinned,
        [styles['layout--fixed-page']]: fixedPageLayout,
      })}
      data-fullscreen-pinned={isFullscreen && hostPinned ? true : undefined}
    >
      <PageSidebarHost
        fullscreen={isFullscreen}
        open={visibleOpened}
        pinned={hostPinned}
        scrollContained={scrollContainedHost}
        sidebar={sidebar}
      >
        {isFullscreen ? (
          <div className={styles['main-column--fullscreen']}>{children}</div>
        ) : (
          <>
            {topNavigation != null ? (
              <div className={styles['top-navigation-slot']}>
                {topNavigation}
              </div>
            ) : null}
            <div className={styles['main-column']}>{children}</div>
          </>
        )}
      </PageSidebarHost>
    </div>
  );
}
